/**
 * stepCommand — the point a chat `{Step:direction:count}` walks to, in the
 * avatar's own frame. Pure. See tests/unit/stepCommand.test.mjs.
 */
import { MIN_WALK_M, MAX_WALK_M } from './walkHerePlan.js'
import { snapToNavmesh } from './bodyMotionPath.js'

/** Just over MIN_WALK_M, which can plan a hair short and be refused. */
export const STEP_MIN_M = MIN_WALK_M + 0.05

/**
 * The point a step ends at, or null if unusable.
 *
 * facingXZ is `[sin yaw, cos yaw]` (yaw 0 faces +Z); right = (-fz, fx).
 * Snapped onto the navmesh so a step toward a wall doesn't walk through it.
 *
 * @param {number[]} position  [x, y, z]
 * @param {number[]} facingXZ  [fx, fz]
 * @param {{direction:string, meters:number}} step
 * @param {object} [navmesh]   a NavMesh instance
 * @returns {number[]|null}    [x, y, z]
 */
export function stepPoint(position, facingXZ, step, navmesh = null) {
  if (!Array.isArray(position) || position.length < 3 || !position.every(Number.isFinite)) return null
  if (!Array.isArray(facingXZ) || !Number.isFinite(facingXZ[0]) || !Number.isFinite(facingXZ[1])) return null
  const len = Math.hypot(facingXZ[0], facingXZ[1])
  if (len < 1e-6) return null
  const fx = facingXZ[0] / len, fz = facingXZ[1] / len

  const dirs = {
    forward: [fx, fz],
    back: [-fx, -fz],
    right: [-fz, fx],
    left: [fz, -fx],
  }
  const d = dirs[step?.direction]
  const meters = Number(step?.meters)
  if (!d || !Number.isFinite(meters) || meters <= 0) return null
  const m = Math.min(MAX_WALK_M, Math.max(STEP_MIN_M, meters))

  let x = position[0] + d[0] * m
  let z = position[2] + d[1] * m
  if (navmesh) {
    const snapped = snapToNavmesh(navmesh, x, z)
    if (Number.isFinite(snapped.dist)) [x, z] = snapped.xz
  }
  return [x, position[1], z]
}
